import { defineStore } from 'pinia'
import { ref } from 'vue'
import { storage } from '@/core/storage'
import { detectLocale } from '@/plugins/i18n'
import type { SupportedLocale } from '@/plugins/i18n'

const LANG_KEY = 'codeboard_lang'

export interface ProviderMeta {
  type: string        // e.g. 'claude' | 'gemini' (API may expose more)
  displayName: string // Display name shown in UI
  requiresApiKey: boolean
  apiKeyPlaceholder: string
  apiKeyHelpUrl: string
}

export interface AiModelInfo {
  id: string
  name: string
  description: string
  category: 'fast' | 'smart' | 'flagship'
  provider: string
  requiresApiKey: boolean
}

export const useSettingsStore = defineStore('settings', () => {
  const language = ref<SupportedLocale>(detectLocale())

  const selectedModel = ref<string | null>(null)
  const selectedProvider = ref<string>('claude')
  const availableModels = ref<string[]>([])
  const providerMeta = ref<ProviderMeta[]>([])
  const modelsByProvider = ref<Record<string, AiModelInfo[]>>({})
  const customApiKeys = ref<Record<string, string>>({})

  function setLanguage(lang: SupportedLocale) {
    language.value = lang
    storage.setItem(LANG_KEY, lang)
    document.documentElement.lang = lang
  }

  function setSelectedModel(model: string | null, provider?: string) {
    selectedModel.value = model
    if (provider) selectedProvider.value = provider
  }

  function setAvailableModels(models: string[]) {
    availableModels.value = models
  }

  function setProviderMeta(meta: ProviderMeta[]) {
    providerMeta.value = meta
  }

  function setModelsByProvider(models: Record<string, AiModelInfo[]>) {
    modelsByProvider.value = models
  }

  function setCustomApiKey(provider: string, key: string) {
    customApiKeys.value = { ...customApiKeys.value, [provider]: key }
  }

  function removeCustomApiKey(provider: string) {
    const copy = { ...customApiKeys.value }
    delete copy[provider]
    customApiKeys.value = copy
  }

  function hasApiKey(provider: string): boolean {
    return !!customApiKeys.value[provider]
  }

  function $reset() {
    selectedModel.value = null
    selectedProvider.value = 'claude'
    availableModels.value = []
    providerMeta.value = []
    modelsByProvider.value = {}
    customApiKeys.value = {}
  }

  return {
    language,
    selectedModel,
    selectedProvider,
    availableModels,
    providerMeta,
    modelsByProvider,
    customApiKeys,
    setLanguage,
    setSelectedModel,
    setAvailableModels,
    setProviderMeta,
    setModelsByProvider,
    setCustomApiKey,
    removeCustomApiKey,
    hasApiKey,
    $reset,
  }
})
